import { Combobox, useCombobox } from "@mantine/core";
import { useEffect, useState } from "react";

import { Icon } from "../../../../assets/icons/Icon";
import Button from "../../../mantine/button/Button";
import style from "./SelectFilterButton.module.scss";

interface FilterOption {
  value: string;
  label: string;
}

interface SelectFilterButtonProps {
  filterOptions: FilterOption[];
  onSelect?: (selected: string[]) => void;
  value?: string[];
}

const SelectFilterButton: React.FC<SelectFilterButtonProps> = ({
  filterOptions,
  onSelect,
  value = [],
}) => {
  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      combobox.focusTarget();
    },
    onDropdownOpen: () => {
      // Add combobox.focusSearchInput() here if <Combobox.Search> is added.
      // Without it Mantine crashes — searchRef is null when there's no search input.
    },
  });

  // Initialize selectedValues with value if provided
  const [selectedValues, setSelectedValues] = useState<string[]>(value || []);

  // Update selectedValues when value prop changes
  useEffect(() => {
    if (value && JSON.stringify(value) !== JSON.stringify(selectedValues)) {
      setSelectedValues(value);
    }
  }, [value]);

  const toggleValue = (val: string) => {
    setSelectedValues((prev) => {
      const newSelected = prev.includes(val)
        ? prev.filter((v) => v !== val)
        : [...prev, val];
      if (onSelect) {
        onSelect(newSelected);
      }

      return newSelected;
    });
  };

  const options = filterOptions?.map((item) => {
    // Check if the current item's value is in the selectedValues or value array
    const isChecked = Array.isArray(value)
      ? value.includes(item.value)
      : selectedValues.includes(item.value);

    return (
      <Combobox.Option value={item.value} key={item.value}>
        <div className={style.selectFilterOption}>
          <input type="checkbox" readOnly checked={isChecked} />
          {item.label}
        </div>
      </Combobox.Option>
    );
  });

  return (
    <div className={style.selectFilter}>
      <Combobox
        store={combobox}
        withinPortal={false}
        onOptionSubmit={(val) => {
          toggleValue(val);
        }}
      >
        <Combobox.Target>
          <Button
            variant="outlinedIcon"
            component="button"
            type="button"
            leftSection={<Icon iconName="FilterIcon" />}
            onClick={() => combobox.toggleDropdown()}
            data-tooltip-id="page-filters-tooltip"
            data-tooltip-content="Manage Filters"
          />
        </Combobox.Target>

        <Combobox.Dropdown className={style.selectFilterDropdown}>
          <Combobox.Options>
            {options?.length > 0 ? (
              options
            ) : (
              <Combobox.Empty>Nothing found</Combobox.Empty>
            )}
          </Combobox.Options>
        </Combobox.Dropdown>
      </Combobox>
    </div>
  );
};

export default SelectFilterButton;
